import { MapPin, Pencil, Plus, Star, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useConfirm } from '@/components/common/ConfirmDialog'
import { usePermission } from '@/features/auth/store'
import { useDeactivateAddress } from '../hooks'
import { ADDRESS_KIND_LABELS, useAddressFormSheet } from './AddressFormSheet'
import type { AddressDto, Customer } from '../types'

function formatAddress(a: AddressDto): string {
  const parts = [a.street, [a.postalCode, a.city].filter(Boolean).join(' ')].filter(Boolean) as string[]
  if (parts.length === 0) return '(brak danych)'
  return parts.join(', ')
}

interface AddressRowProps {
  address: AddressDto
  canEdit: boolean
  onEdit: () => void
  onDeactivate: () => void
}

function AddressRow({ address, canEdit, onEdit, onDeactivate }: AddressRowProps) {
  return (
    <div className="flex items-start gap-3 border-b py-3 last:border-0">
      <MapPin className="size-4 mt-0.5 text-muted-foreground" />
      <div className="flex flex-1 flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <span className="font-medium">{formatAddress(address)}</span>
          {address.isPrimary && (
            <Badge variant="default" className="gap-1 text-xs">
              <Star className="size-3 fill-current" />
              Główny
            </Badge>
          )}
        </div>
        <span className="text-xs text-muted-foreground">
          {ADDRESS_KIND_LABELS[address.kind]} · {address.countryCode}
        </span>
      </div>
      {canEdit && (
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={onEdit} aria-label="Edytuj">
            <Pencil className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={onDeactivate}
            aria-label="Usuń"
            className="text-destructive hover:text-destructive"
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      )}
    </div>
  )
}

export function CustomerAddressesTab({ customer }: { customer: Customer }) {
  const confirm = useConfirm()
  const canEdit = usePermission('partners.customer.edit')
  const sheet = useAddressFormSheet(customer.id)
  const deactivate = useDeactivateAddress(customer.id)

  const handleDeactivate = async (address: AddressDto) => {
    if (address.id == null) return
    const ok = await confirm({
      title: 'Usunąć adres?',
      description: `"${formatAddress(address)}"`,
      confirmLabel: 'Usuń',
      variant: 'destructive',
    })
    if (!ok) return
    try {
      await deactivate.mutateAsync(address.id)
      toast.success('Adres usunięty')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Błąd usuwania')
    }
  }

  return (
    <section className="rounded-lg border bg-card p-4">
      <header className="mb-2 flex items-center justify-between">
        <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">Adresy</h2>
        {canEdit && (
          <Button variant="outline" size="sm" className="gap-2" onClick={sheet.openAdd}>
            <Plus className="size-4" />
            Dodaj adres
          </Button>
        )}
      </header>
      {customer.addresses.length === 0 ? (
        <p className="py-3 text-sm text-muted-foreground">Brak adresów.</p>
      ) : (
        customer.addresses.map((a) => (
          <AddressRow
            key={a.id ?? `${a.kind}-${a.street}`}
            address={a}
            canEdit={canEdit}
            onEdit={() => sheet.openEdit(a)}
            onDeactivate={() => handleDeactivate(a)}
          />
        ))
      )}
      {sheet.sheet}
    </section>
  )
}
